/*
 * lib/pwa-update.js
 *
 * Service-worker update-flow. vite-plugin-pwa staat sinds 2026-06-20 op
 * `registerType: 'prompt'`, dus we krijgen een handle waarmee we zelf
 * beslissen wanneer een nieuwe versie geactiveerd wordt.
 *
 * Waarom geen autoUpdate? Een PWA op het homescreen sluit nooit echt af
 * (de tab blijft in de achtergrond hangen), dus SKIP_WAITING moet door
 * ons getriggerd worden. Anders blijft de gebruiker op de oude versie
 * tot hij de app verwijdert en opnieuw installeert — exact wat Lander
 * meermaals heeft moeten doen.
 *
 * Flow:
 *   1. SW registreert bij boot.
 *   2. Periodieke check (elk uur) of er een nieuwe versie klaar staat.
 *   3. Zodra er één klaar staat → toast "Nieuwe versie beschikbaar".
 *   4. Tap op toast → updateSW(true) → SKIP_WAITING + reload.
 *
 * `checkForUpdateNow()` wordt vanuit Instellingen → "Check op updates"
 * aangeroepen voor een handmatige refresh.
 */

import { registerSW } from 'virtual:pwa-register';

let updateSWFn = null;
let needRefresh = false;

/** Bouw het toast-DOM. Eén keer per pagina-leven gemount. */
function ensureToast() {
  let el = document.getElementById('pwa-update-toast');
  if (el) return el;
  el = document.createElement('div');
  el.id = 'pwa-update-toast';
  el.className = 'pwa-update-toast';
  el.hidden = true;
  el.innerHTML = `
    <span class="pwa-update-msg">✨ Nieuwe versie beschikbaar</span>
    <button class="pwa-update-btn" type="button">Vernieuwen</button>
    <button class="pwa-update-dismiss" type="button" aria-label="Sluit">×</button>
  `;
  el.querySelector('.pwa-update-btn').addEventListener('click', () => {
    if (updateSWFn) updateSWFn(true);
  });
  el.querySelector('.pwa-update-dismiss').addEventListener('click', () => {
    el.hidden = true;
  });
  document.body.appendChild(el);
  return el;
}

function showToast() {
  const el = ensureToast();
  el.hidden = false;
}

/** Init: registreer SW en hou een handle bij voor handmatige checks. */
export function initPwaUpdate() {
  if (typeof window === 'undefined') return;
  try {
    updateSWFn = registerSW({
      onNeedRefresh() {
        needRefresh = true;
        showToast();
      },
      onOfflineReady() {
        // Eerste keer geïnstalleerd — niets te tonen, geen interruptie.
      },
      onRegisteredSW(_swUrl, registration) {
        if (!registration) return;
        // Elk uur even pollen — homescreen-PWA's draaien soms dagen door
        // zonder dat de browser uit zichzelf een update zoekt.
        setInterval(() => {
          registration.update().catch(() => {});
        }, 60 * 60 * 1000);
      }
    });
  } catch (err) {
    // dev-mode zonder SW: virtual:pwa-register kan dan stub-erig zijn.
    console.info('PWA register skipped:', err?.message);
  }
}

/**
 * Handmatige check vanuit Instellingen.
 * Returnt status zodat de UI een korte feedback kan tonen:
 *   'updated'   — er stond al een nieuwe versie klaar (toast wordt getoond).
 *   'checking'  — registration.update() succesvol getriggerd, wacht af.
 *   'unknown'   — geen registration handle, niets gedaan.
 */
export async function checkForUpdateNow() {
  if (needRefresh) {
    showToast();
    return 'updated';
  }
  if (!('serviceWorker' in navigator)) return 'unknown';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return 'unknown';
    await reg.update();
    return 'checking';
  } catch {
    return 'unknown';
  }
}
